/**
 * MAC WELDING TRESTLE CONFIGURATOR — ENGINE
 * ---------------------------------------------------------------
 * Uses the SAME wc-* visual classes as the Welding Table Configurator
 * so the cards, selected states, chips and swatches match exactly.
 * ---------------------------------------------------------------
 */

(function () {
  "use strict";

  class WeldingTrestleConfigurator {
    constructor(root, data) {
      this.root = root;
      this.data = data;

      this.state = {
        holeSystem: null,
        trestleType: null,
        thickness: null,
        length: null,
        width: null,
        baseOption: null,
        finish: null,
        comments: ""
      };

      this.els = {
        step1: root.querySelector(
          '[data-step="1"] .wtc-options, [data-step="1"] .wc-options'
        ),

        step2: root.querySelector(
          '[data-step="2"] .wtc-options, [data-step="2"] .wc-options'
        ),

        step3: root.querySelector(
          '[data-step="3"] .wtc-options, [data-step="3"] .wc-options'
        ),

        step4: root.querySelector(
          '[data-step="4"] .wtc-options, [data-step="4"] .wc-options'
        ),

        step5: root.querySelector(
          '[data-step="5"] .wtc-options, [data-step="5"] .wc-options'
        ),

        step6: root.querySelector(
          '[data-step="6"] .wtc-options, [data-step="6"] .wc-options'
        ),

        step7: root.querySelector(
          '[data-step="7"] .wtc-options, [data-step="7"] .wc-options'
        ),

        comments:
          root.querySelector('[data-role="comments"]'),

        commentsCount:
          root.querySelector('[data-role="comments-count"]'),

        previewImage:
          root.querySelector('[data-role="preview-image"]'),

        sidebar:
          root.querySelector(".wc__sidebar, .wtc__sidebar"),

        summary:
          root.querySelector('[data-role="summary"]'),

        price:
          root.querySelector('[data-role="price"]'),

        addToCart:
          root.querySelector('[data-action="add-to-cart"]'),

        downloadPdf:
          root.querySelector('[data-action="download-pdf"]'),

        reset:
          root.querySelector('[data-action="reset"]')
      };

      this.init();
    }


    /* ==========================================================
       INITIALISE
       ========================================================== */

    init() {
      this.applyDefaults();

      this.ensurePreviewImage();

      this.bindEvents();

      this.refresh();
    }


    /* ==========================================================
       DEFAULT OPTIONS
       ========================================================== */

    applyDefaults() {

      if (
        !this.state.holeSystem &&
        (this.data.holeSystems || []).length
      ) {
        this.state.holeSystem =
          this.data.holeSystems[0].id;
      }


      if (
        !this.state.trestleType &&
        (this.data.trestleTypes || []).length
      ) {
        this.state.trestleType =
          this.data.trestleTypes[0].id;
      }


      if (
        !this.state.thickness &&
        (this.data.thicknesses || []).length
      ) {
        this.state.thickness =
          this.data.thicknesses[0].id;
      }


      if (
        !this.state.length &&
        (this.data.lengths || []).length
      ) {
        this.state.length =
          this.data.lengths[0].id;
      }


      if (
        !this.state.width &&
        (this.data.widths || []).length
      ) {
        this.state.width =
          this.data.widths[0].id;
      }


      if (
        !this.state.baseOption &&
        (this.data.baseOptions || []).length
      ) {
        this.state.baseOption =
          this.data.baseOptions[0].id;
      }


      if (
        !this.state.finish &&
        (this.data.finishes || []).length
      ) {
        this.state.finish =
          this.data.finishes[0].id;
      }
    }


    /* ==========================================================
       FIND OPTIONS
       ========================================================== */

    findById(list, id) {

      return (
        list || []
      ).find(
        item =>
          String(item.id) === String(id)
      ) || null;
    }


    get selectedType() {

      return this.findById(
        this.data.trestleTypes,
        this.state.trestleType
      );
    }


    get selectedBaseOption() {

      return this.findById(
        this.data.baseOptions,
        this.state.baseOption
      );
    }


    get selectedFinish() {

      return this.findById(
        this.data.finishes,
        this.state.finish
      );
    }


    /* ==========================================================
       PRICE FORMAT
       ========================================================== */

    fmtPrice(value) {

      return (
        this.data.currency || "£"
      ) + Number(
        value || 0
      ).toLocaleString(
        undefined,
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      );
    }


    /* ==========================================================
       ICON URL
       ========================================================== */

    iconUrl(filename) {

      if (!filename) {
        return "";
      }


      if (
        /^https?:\/\//i.test(filename)
      ) {
        return filename;
      }


      if (
        window.WT_ICON_URLS &&
        window.WT_ICON_URLS[filename]
      ) {
        return window.WT_ICON_URLS[
          filename
        ];
      }


      return filename;
    }


    /* ==========================================================
       ICON IMAGE
       ========================================================== */

    iconImg(
      filename,
      alt,
      className = "wc-icon"
    ) {

      if (!filename) {
        return "";
      }


      return `
        <img
          src="${this.iconUrl(filename)}"
          alt="${this.escapeAttr(alt || "")}"
          class="${className}"
          loading="lazy"
        >
      `;
    }


    /* ==========================================================
       FORCE EXACT TABLE LAYOUT CLASSES

       This is important.

       The old trestle CSS used .wtc-options.

       We remove that class after the JS has found the element,
       then give it the exact same layout class used by the
       welding table configurator.
       ========================================================== */

    setTargetLayout(
      target,
      layoutClass
    ) {

      if (!target) {
        return;
      }


      target.classList.remove(
        "wtc-options",
        "wc-options",
        "wc-sizes",
        "wc-products"
      );


      target.classList.add(
        layoutClass
      );
    }


    /* ==========================================================
       CONFIGURATION KEY
       ========================================================== */

    combinationKey() {

      return [
        this.state.holeSystem,
        this.state.thickness,
        this.state.length,
        this.state.width
      ].join("-");
    }


    /* ==========================================================
       BASE PRICE
       ========================================================== */

    getBasePrice() {

      const h =
        this.state.holeSystem;

      const t =
        this.state.thickness;

      const l =
        this.state.length;

      const w =
        this.state.width;


      const prices =
        this.data.basePrices || {};


      if (
        prices[h] &&
        prices[h][t] &&
        prices[h][t][l] &&
        Object.prototype.hasOwnProperty.call(
          prices[h][t][l],
          w
        )
      ) {

        const value =
          Number(
            prices[h][t][l][w]
          );


        return Number.isFinite(value)
          ? value
          : null;
      }


      return null;
    }


    /* ==========================================================
       OPTION PRICE
       ========================================================== */

    getOptionPrice(option) {

      if (!option) {
        return 0;
      }


      if (
        option.price === null ||
        option.price === undefined ||
        option.price === ""
      ) {
        return 0;
      }


      const value =
        Number(option.price);


      return Number.isFinite(value)
        ? value
        : 0;
    }


    /* ==========================================================
       TOTAL PRICE
       ========================================================== */

    calcPrice() {

      const base =
        this.getBasePrice();


      if (
        base === null
      ) {
        return null;
      }


      return (
        base +

        this.getOptionPrice(
          this.selectedBaseOption
        ) +

        this.getOptionPrice(
          this.selectedFinish
        )
      );
    }


    /* ==========================================================
       SKU
       ========================================================== */

    generateSku() {

      if (
        !this.isSelectionComplete()
      ) {
        return null;
      }


      const baseCode =
        this.selectedBaseOption
          ? this.selectedBaseOption.skuCode
          : "";


      const finishCode =
        this.selectedFinish
          ? this.selectedFinish.skuCode
          : "";


      return [
        this.data.skuPrefix || "UT",
        this.state.length,
        this.state.width,
        this.state.holeSystem,
        this.state.thickness,
        baseCode,
        finishCode
      ]
        .filter(Boolean)
        .join("-");
    }


    /* ==========================================================
       SHOPIFY VARIANT
       ========================================================== */

    getShopifyVariantId() {

      const raw =
        (
          this.data.shopifyVariantIds ||
          {}
        )[
          this.combinationKey()
        ];


      const id =
        Number(raw);


      return (
        Number.isFinite(id) &&
        id > 0
      )
        ? id
        : null;
    }


    /* ==========================================================
       CONFIG COMPLETE
       ========================================================== */

    isSelectionComplete() {

      return !!(
        this.state.holeSystem &&
        this.state.trestleType &&
        this.state.thickness &&
        this.state.length &&
        this.state.width &&
        this.state.baseOption &&
        this.state.finish
      );
    }


    /* ==========================================================
       PRICE TEXT FOR OPTION
       ========================================================== */

    getPriceText(
      item,
      showPrice
    ) {

      if (!showPrice) {
        return "";
      }


      if (
        item.price === null ||
        item.price === undefined ||
        item.price === ""
      ) {
        return "";
      }


      const price =
        Number(item.price);


      if (
        !Number.isFinite(price)
      ) {
        return "";
      }


      return price > 0
        ? `+${this.fmtPrice(price)}`
        : "Included";
    }


    /* ==========================================================
       RENDER OPTION GROUP

       This is the main change.

       Each type of trestle option now uses the SAME markup
       classes as the corresponding welding-table option.
       ========================================================== */

    renderChoiceGroup(
      target,
      items,
      selectedId,
      action,
      options = {}
    ) {

      if (!target) {
        return;
      }


      const list =
        items || [];


      /* ========================================================
         LENGTH / WIDTH

         SAME AS TABLE SIZE BUTTONS
         ======================================================== */

      if (
        action === "length" ||
        action === "width"
      ) {

        this.setTargetLayout(
          target,
          "wc-sizes"
        );


        target.innerHTML =
          list.map(item => {

            const selected =
              String(item.id) ===
              String(selectedId)
                ? " is-selected"
                : "";


            return `
              <button
                type="button"
                class="wc-chip${selected}"
                data-action="${action}"
                data-value="${this.escapeAttr(item.id)}"
              >

                ${this.escapeHtml(item.label)}

              </button>
            `;

          }).join("");


        return;
      }


      /* ========================================================
         TRESTLE TYPE

         SAME AS TABLE TYPE PRODUCT CARDS
         ======================================================== */

      if (
        action === "trestle-type"
      ) {

        this.setTargetLayout(
          target,
          "wc-products"
        );


        target.innerHTML =
          list.map(item => {

            const selected =
              String(item.id) ===
              String(selectedId)
                ? " is-selected"
                : "";


            const image =
              item.icon ||
              item.image ||
              item.previewImage ||
              "";


            return `
              <button
                type="button"
                class="wc-product${selected}"
                data-action="${action}"
                data-value="${this.escapeAttr(item.id)}"
              >

                ${
                  image

                    ? this.iconImg(
                        image,
                        item.label,
                        "wc-product__img"
                      )

                    : `
                      <span
                        class="
                          wc-product__img
                          wc-product__img--placeholder
                        "
                      ></span>
                    `
                }


                <span class="wc-product__name">

                  ${this.escapeHtml(item.label)}

                </span>

              </button>
            `;

          }).join("");


        return;
      }


      /* ========================================================
         LEG FINISH

         SAME AS TABLE COLOUR SWATCH
         ======================================================== */

      if (
        action === "finish"
      ) {

        this.setTargetLayout(
          target,
          "wc-options"
        );


        target.innerHTML =
          list.map(item => {

            const selected =
              String(item.id) ===
              String(selectedId)
                ? " is-selected"
                : "";


            const image =
              item.icon ||
              item.image ||
              "";


            const priceText =
              this.getPriceText(
                item,
                options.showPrice
              );


            let colorKey =
              String(
                item.color ||
                item.id ||
                item.label ||
                ""
              )
                .toLowerCase()
                .trim()
                .replace(
                  /\s+/g,
                  "-"
                );


            if (
              colorKey === "raw" ||
              colorKey === "raw-no-paint" ||
              colorKey === "raw-no-powder-coat"
            ) {

              colorKey =
                "raw-no-paint";
            }


            return `
              <button
                type="button"
                class="
                  wc-swatch
                  ${image ? "wc-swatch--icon" : ""}
                  ${selected}
                "
                data-action="${action}"
                data-value="${this.escapeAttr(item.id)}"
                title="${this.escapeAttr(item.label)}"
              >

                ${
                  image

                    ? this.iconImg(
                        image,
                        item.label,
                        "wc-swatch__icon"
                      )

                    : `
                      <span
                        class="wc-swatch__dot"
                        data-color="${this.escapeAttr(colorKey)}"
                      ></span>
                    `
                }


                <span class="wc-swatch__label">

                  ${this.escapeHtml(item.label)}

                </span>


                ${
                  priceText

                    ? `
                      <span class="wc-card__price">

                        ${priceText}

                      </span>
                    `

                    : ""
                }

              </button>
            `;

          }).join("");


        return;
      }


      /* ========================================================
         HOLE SYSTEM
         THICKNESS
         BASE / FEET OPTION

         SAME AS TABLE ICON CARDS
         ======================================================== */

      this.setTargetLayout(
        target,
        "wc-options"
      );


      target.innerHTML =
        list.map(item => {

          const selected =
            String(item.id) ===
            String(selectedId)
              ? " is-selected"
              : "";


          const image =
            item.icon ||
            item.image ||
            "";


          const priceText =
            this.getPriceText(
              item,
              options.showPrice
            );


          return `
            <button
              type="button"
              class="
                wc-card
                ${image ? "wc-card--icon" : ""}
                ${selected}
              "
              data-action="${action}"
              data-value="${this.escapeAttr(item.id)}"
            >

              ${
                image

                  ? this.iconImg(
                      image,
                      item.label
                    )

                  : ""
              }


              <span class="wc-card__label">

                ${this.escapeHtml(item.label)}

              </span>


              ${
                priceText

                  ? `
                    <span class="wc-card__price">

                      ${priceText}

                    </span>
                  `

                  : ""
              }

            </button>
          `;

        }).join("");
    }


    /* ==========================================================
       RENDER ALL OPTIONS
       ========================================================== */

    renderAllChoices() {

      this.renderChoiceGroup(
        this.els.step1,
        this.data.holeSystems,
        this.state.holeSystem,
        "hole-system"
      );


      this.renderChoiceGroup(
        this.els.step2,
        this.data.trestleTypes,
        this.state.trestleType,
        "trestle-type"
      );


      this.renderChoiceGroup(
        this.els.step3,
        this.data.thicknesses,
        this.state.thickness,
        "thickness"
      );


      this.renderChoiceGroup(
        this.els.step4,
        this.data.lengths,
        this.state.length,
        "length"
      );


      this.renderChoiceGroup(
        this.els.step5,
        this.data.widths,
        this.state.width,
        "width"
      );


      this.renderChoiceGroup(
        this.els.step6,
        this.data.baseOptions,
        this.state.baseOption,
        "base-option",
        {
          showPrice: true
        }
      );


      this.renderChoiceGroup(
        this.els.step7,
        this.data.finishes,
        this.state.finish,
        "finish",
        {
          showPrice: true
        }
      );
    }


    /* ==========================================================
       CREATE PREVIEW IMAGE IF LIQUID DIDN'T OUTPUT ONE
       ========================================================== */

    ensurePreviewImage() {

      if (
        this.els.previewImage ||
        !this.els.sidebar
      ) {
        return;
      }


      const image =
        document.createElement("img");


      image.className =
        "wtc__preview";


      image.setAttribute(
        "data-role",
        "preview-image"
      );


      image.setAttribute(
        "loading",
        "lazy"
      );


      image.alt =
        "";


      this.els.sidebar.insertBefore(
        image,
        this.els.sidebar.firstChild
      );


      this.els.previewImage =
        image;
    }


    /* ==========================================================
       PREVIEW
       ========================================================== */

    renderPreview() {

      if (
        !this.els.previewImage
      ) {
        return;
      }


      const type =
        this.selectedType;


      if (
        !type ||
        !type.previewImage
      ) {

        this.els.previewImage.style.display =
          "none";


        return;
      }


      const url =
        this.iconUrl(
          type.previewImage
        );


      if (
        url &&
        this.els.previewImage.getAttribute(
          "src"
        ) !== url
      ) {

        this.els.previewImage.setAttribute(
          "src",
          url
        );
      }


      this.els.previewImage.alt =
        `${type.label} welding trestle`;


      this.els.previewImage.style.display =
        "";
    }


    /* ==========================================================
       SUMMARY
       ========================================================== */

    renderSummary() {

      if (
        !this.els.summary
      ) {
        return;
      }


      const type =
        this.selectedType;


      const baseOption =
        this.selectedBaseOption;


      const finish =
        this.selectedFinish;


      const sku =
        this.generateSku();


      const rows =
        [];


      rows.push([
        "Hole Size (System)",

        this.state.holeSystem === "16D"
          ? "16mm"
          : "28mm"
      ]);


      if (type) {

        rows.push([
          "Trestle Type",
          type.label
        ]);
      }


      rows.push([
        "Material Thickness",
        `${this.state.thickness}mm`
      ]);


      rows.push([
        "Trestle Length",
        `${this.state.length}mm`
      ]);


      rows.push([
        "Trestle Width",
        `${this.state.width}mm`
      ]);


      if (baseOption) {

        rows.push([
          "Leg End Option",
          baseOption.label
        ]);
      }


      if (finish) {

        rows.push([
          "Leg Finish",
          finish.label
        ]);
      }


      if (sku) {

        rows.push([
          "SKU",
          sku
        ]);
      }


      if (
        this.state.comments
      ) {

        rows.push([
          "Comments",
          this.state.comments
        ]);
      }


      this.els.summary.innerHTML =
        rows.map(
          ([label, value]) => `
            <div class="wc-summary__row">

              <span>
                ${this.escapeHtml(label)}
              </span>

              <span>
                ${this.escapeHtml(value)}
              </span>

            </div>
          `
        ).join("");


      this.renderPrice();

      this.renderButtons();
    }


    /* ==========================================================
       PRICE
       ========================================================== */

    renderPrice() {

      if (
        !this.els.price
      ) {
        return;
      }


      const priceExVat =
        this.calcPrice();


      if (
        priceExVat === null
      ) {

        this.els.price.innerHTML =
          `<span>Price TBC</span>`;


        return;
      }


      const vatRate =
        Number.isFinite(
          Number(
            this.data.vatRate
          )
        )

          ? Number(
              this.data.vatRate
            )

          : 0.20;


      const priceIncVat =
        priceExVat *
        (
          1 +
          vatRate
        );


      this.els.price.innerHTML = `

        <span
          style="
            display:block;
            font-size:18px;
            font-weight:700;
          "
        >

          ${this.fmtPrice(priceExVat)}

          <small
            style="
              font-size:10px;
            "
          >
            ex VAT
          </small>

        </span>


        <span
          style="
            display:block;
            font-size:24px;
            font-weight:800;
          "
        >

          ${this.fmtPrice(priceIncVat)}

          <small
            style="
              font-size:10px;
            "
          >
            inc VAT
          </small>

        </span>

      `;
    }


    /* ==========================================================
       BUTTON STATES
       ========================================================== */

    renderButtons() {

      const ready =
        this.isSelectionComplete();


      const variantId =
        this.getShopifyVariantId();


      const price =
        this.calcPrice();


      if (
        this.els.addToCart
      ) {

        this.els.addToCart.disabled =
          !ready ||
          !variantId ||
          price === null;
      }


      if (
        this.els.downloadPdf
      ) {

        const type =
          this.selectedType;


        this.els.downloadPdf.disabled =
          !(
            type &&
            type.drawingPdf
          );
      }
    }


    /* ==========================================================
       BOTTOM STEPPER
       ========================================================== */

    updateStepper() {

      const states = {

        1:
          !!this.state.holeSystem,

        2:
          !!this.state.trestleType,

        3:
          !!this.state.thickness,

        4:
          !!this.state.length,

        5:
          !!this.state.width,

        6:
          !!this.state.baseOption,

        7:
          !!this.state.finish,

        8:
          !!this.state.comments
      };


      Object.keys(
        states
      ).forEach(
        number => {

          const dot =
            this.root.querySelector(
              `[data-stepper="${number}"]`
            );


          if (!dot) {
            return;
          }


          dot.classList.toggle(
            "is-done",
            states[number]
          );
        }
      );
    }


    /* ==========================================================
       REFRESH
       ========================================================== */

    refresh() {

      this.renderAllChoices();

      this.renderPreview();

      this.renderSummary();

      this.updateStepper();
    }


    /* ==========================================================
       EVENTS
       ========================================================== */

    bindEvents() {

      this.root.addEventListener(
        "click",
        event => {

          const button =
            event.target.closest(
              "[data-action]"
            );


          if (
            !button ||
            button.disabled
          ) {
            return;
          }


          const action =
            button.dataset.action;


          const value =
            button.dataset.value;


          switch (action) {


            case "hole-system":

              this.state.holeSystem =
                value;

              break;


            case "trestle-type":

              this.state.trestleType =
                value;

              break;


            case "thickness":

              this.state.thickness =
                value;

              break;


            case "length":

              this.state.length =
                value;

              break;


            case "width":

              this.state.width =
                value;

              break;


            case "base-option":

              this.state.baseOption =
                value;

              break;


            case "finish":

              this.state.finish =
                value;

              break;


            case "add-to-cart":

              this.addToCart();

              return;


            case "download-pdf":

              this.downloadPdf();

              return;


            case "reset":

              this.resetAll();

              break;
          }


          this.applyDefaults();

          this.refresh();
        }
      );


      if (
        this.els.comments
      ) {

        this.els.comments.addEventListener(
          "input",
          event => {

            this.state.comments =
              event.target.value.slice(
                0,
                500
              );


            if (
              this.els.commentsCount
            ) {

              this.els.commentsCount.textContent =
                `${this.state.comments.length} / 500`;
            }


            this.renderSummary();

            this.updateStepper();
          }
        );
      }
    }


    /* ==========================================================
       RESET
       ========================================================== */

    resetAll() {

      this.state = {

        holeSystem: null,

        trestleType: null,

        thickness: null,

        length: null,

        width: null,

        baseOption: null,

        finish: null,

        comments: ""
      };


      if (
        this.els.comments
      ) {

        this.els.comments.value =
          "";
      }


      if (
        this.els.commentsCount
      ) {

        this.els.commentsCount.textContent =
          "0 / 500";
      }


      this.applyDefaults();

      this.refresh();
    }


    /* ==========================================================
       PAYLOAD
       ========================================================== */

    buildPayload() {

      const priceExVat =
        this.calcPrice();


      const vatRate =
        Number.isFinite(
          Number(
            this.data.vatRate
          )
        )

          ? Number(
              this.data.vatRate
            )

          : 0.20;


      return {

        sku:
          this.generateSku(),


        holeSystem:
          this.state.holeSystem,


        trestleType:
          this.selectedType

            ? this.selectedType.label

            : null,


        thickness:
          `${this.state.thickness}mm`,


        length:
          `${this.state.length}mm`,


        width:
          `${this.state.width}mm`,


        baseOption:
          this.selectedBaseOption

            ? this.selectedBaseOption.label

            : null,


        finish:
          this.selectedFinish

            ? this.selectedFinish.label

            : null,


        comments:
          this.state.comments,


        priceExVat,


        priceIncVat:
          priceExVat === null

            ? null

            : priceExVat *
              (
                1 +
                vatRate
              )
      };
    }


    /* ==========================================================
       CART PROPERTIES
       ========================================================== */

    buildCartProperties() {

      const payload =
        this.buildPayload();


      const properties =
        {};


      Object.keys(
        payload
      ).forEach(
        key => {

          const value =
            payload[key];


          if (
            value === null ||
            value === undefined ||
            value === ""
          ) {
            return;
          }


          properties[
            `_${key}`
          ] =
            String(value);
        }
      );


      if (
        payload.priceIncVat !== null
      ) {

        properties.__price =
          Number(
            payload.priceIncVat
          ).toFixed(2);
      }


      properties._all_props =
        JSON.stringify(
          payload
        );


      return properties;
    }


    /* ==========================================================
       ADD TO CART
       ========================================================== */

    addToCart() {

      if (
        !this.isSelectionComplete()
      ) {
        return;
      }


      const variantId =
        this.getShopifyVariantId();


      const price =
        this.calcPrice();


      if (
        !variantId
      ) {

        alert(
          "This trestle configuration has not yet been linked to a Shopify variant ID."
        );


        return;
      }


      if (
        price === null
      ) {

        alert(
          "This trestle configuration does not have a confirmed price yet."
        );


        return;
      }


      const payload = {

        items: [

          {

            id:
              variantId,


            quantity:
              1,


            properties:
              this.buildCartProperties()
          }

        ]

      };


      if (
        this.els.addToCart
      ) {

        this.els.addToCart.disabled =
          true;
      }


      fetch(
        "/cart/add.js",

        {

          method:
            "POST",


          headers: {

            "Content-Type":
              "application/json"
          },


          body:
            JSON.stringify(
              payload
            )
        }
      )


        .then(
          response => {

            if (
              !response.ok
            ) {

              return response
                .json()
                .then(
                  error =>
                    Promise.reject(
                      error
                    )
                );
            }


            return response.json();
          }
        )


        .then(
          () => {

            document.dispatchEvent(

              new CustomEvent(

                "cart:refresh",

                {

                  bubbles:
                    true,


                  detail: {

                    open:
                      true
                  }

                }

              )

            );
          }
        )


        .catch(
          error => {

            console.error(
              "Welding trestle configurator:",
              error
            );


            alert(

              (
                error &&

                (
                  error.description ||
                  error.message
                )
              )

              ||

              "Sorry, something went wrong adding this trestle to your cart."

            );
          }
        )


        .finally(
          () => {

            this.renderButtons();

          }
        );
    }


    /* ==========================================================
       DOWNLOAD PDF
       ========================================================== */

    downloadPdf() {

      const type =
        this.selectedType;


      if (
        !type ||
        !type.drawingPdf
      ) {

        alert(
          "Sorry, a technical drawing is not currently available for this trestle."
        );


        return;
      }


      const link =
        document.createElement(
          "a"
        );


      link.href =
        type.drawingPdf;


      link.target =
        "_blank";


      link.rel =
        "noopener noreferrer";


      document.body.appendChild(
        link
      );


      link.click();


      document.body.removeChild(
        link
      );
    }


    /* ==========================================================
       ESCAPE HTML
       ========================================================== */

    escapeHtml(value) {

      const div =
        document.createElement(
          "div"
        );


      div.textContent =
        String(
          value == null
            ? ""
            : value
        );


      return div.innerHTML;
    }


    /* ==========================================================
       ESCAPE ATTRIBUTE
       ========================================================== */

    escapeAttr(value) {

      return this
        .escapeHtml(value)
        .replace(
          /"/g,
          "&quot;"
        );
    }

  }


  /* ==========================================================
     START CONFIGURATOR
     ========================================================== */

  document.addEventListener(

    "DOMContentLoaded",

    function () {

      document
        .querySelectorAll(
          "[data-welding-trestle-configurator]"
        )
        .forEach(
          root => {

            if (
              window.WELDING_TRESTLE_CONFIGURATOR_DATA
            ) {

              new WeldingTrestleConfigurator(

                root,

                window.WELDING_TRESTLE_CONFIGURATOR_DATA

              );

            }

          }
        );

    }

  );

})();